/**
 * Implementação do repositório de Plan: orquestra datasource remoto e local.
 * Firestore é a fonte de verdade; o cache local espelha o remoto após cada leitura.
 */

import type { Plan } from '@/core/domain';
import type { PlanRepository } from '../../domain/repositories/plan-repository';
import type { PlanRemoteDataSource } from '../datasources/remote';
import type { PlanLocalDataSource } from '../datasources/local';

async function replaceLocalCache(local: PlanLocalDataSource, plans: Plan[]): Promise<void> {
  await local.clear().catch(() => {});
  for (const plan of plans) {
    await local.create(plan).catch(() => {});
  }
}

export class PlanRepositoryImpl implements PlanRepository {
  constructor(
    private readonly remote: PlanRemoteDataSource,
    private readonly local: PlanLocalDataSource
  ) {}

  async getById(id: string): Promise<Plan | null> {
    try {
      const fromRemote = await this.remote.getById(id);
      if (fromRemote != null) {
        await this.local.create(fromRemote).catch(() => {});
        return fromRemote;
      }
      await this.local.delete(id).catch(() => {});
      return null;
    } catch {
      return this.local.getById(id);
    }
  }

  async getAll(): Promise<Plan[]> {
    try {
      const fromRemote = await this.remote.getAll();
      await replaceLocalCache(this.local, fromRemote);
      return fromRemote;
    } catch {
      return this.local.getAll();
    }
  }

  async create(plan: Plan): Promise<Plan> {
    const created = await this.remote.create(plan);
    await this.local.create(created).catch(() => {});
    return created;
  }

  async update(id: string, plan: Partial<Plan>): Promise<Plan> {
    const updated = await this.remote.update(id, plan);
    await this.local.update(id, updated).catch(() => {});
    return updated;
  }

  async delete(id: string): Promise<void> {
    await this.remote.delete(id);
    await this.local.delete(id).catch(() => {});
  }
}
